import {
  defineClientEnv,
  httpsUrl,
  httpUrl,
  nonEmpty,
} from '@up2stay/up2promo-commons/env';
import { z } from 'zod';

/**
 * Public build env of the customer app, web and native (ADR-0029).
 * Keep `.env.example` in sync.
 */
export const envSchema = defineClientEnv({
  /**
   * API origin, no `/api/v1`. Production builds (Vercel, Capawesome, local
   * `cap:sync`) always use it as-is; the dev server may leave it unset and
   * derives the API from the page host (`file-url.ts`).
   */
  VITE_API_URL: { schema: httpUrl(), required: 'build' },
  /** Dev server only: API port when VITE_API_URL is unset (default 3000). */
  VITE_API_PORT: {
    schema: z.string().regex(/^\d+$/, 'must be a port number'),
    required: 'optional',
  },
  /** Profile viewer origin for public profile links. */
  VITE_PROFILE_VIEWER_DOMAIN: { schema: httpUrl(), required: 'build' },
  /**
   * Dev server only: origin for encoded product links (LAN / tunnel tests).
   * Production builds always print https://up2.page.
   */
  VITE_PRODUCT_LINK_ORIGIN: { schema: httpUrl(), required: 'optional' },
  /** Google Maps JavaScript API key; maps show "unavailable" without it. */
  VITE_GOOGLE_MAPS_API_KEY: { schema: nonEmpty(), required: 'deployed' },
  VITE_SALES_EMAIL: { schema: z.email(), required: 'optional' },
  VITE_CALENDLY_URL: { schema: httpsUrl(), required: 'optional' },
  /** Orientation tour welcome video (unset = placeholder). */
  VITE_GUIDANCE_WELCOME_VIDEO_URL: {
    schema: httpsUrl().or(z.string().startsWith('/')),
    required: 'optional',
  },
  /** Tour Kit Pro; without a key, Pro features stay off on deployed hosts. */
  VITE_TOUR_KIT_LICENSE_KEY: { schema: nonEmpty(), required: 'optional' },
  VITE_TOUR_KIT_ORGANIZATION_ID: { schema: nonEmpty(), required: 'optional' },
  VITE_POSTHOG_KEY: { schema: nonEmpty(), required: 'deployed' },
  VITE_POSTHOG_HOST: { schema: httpUrl(), required: 'optional' },
  VITE_APP_LANGUAGE_HEADER: { schema: nonEmpty(), required: 'optional' },
  /** Reported with the push token; unset on most builds. */
  VITE_APP_VERSION: { schema: nonEmpty(), required: 'optional' },
  /** Dev server only: `true` enables request / perf logging. */
  VITE_DEV_DEBUG: { schema: z.enum(['true', 'false']), required: 'optional' },
});
